// Tabs (009 revision 6, FR-044, FR-046; shadcn/ui `tabs`, restyled): the one tab bar of every app, on Radix Tabs
// (arrow keys move between tabs, Home and End jump, right to left follows `dir`). The `line` variant is the page and
// section tabs (an underline under the current tab); `pill` is a segmented bar. The list scrolls sideways inside
// itself when the tabs do not fit (`data-ui-scroll="x"`), so the page never does. Tabs that are routes use
// `PageTabs` (src/shell), which renders links with the same look.
import { cva, type VariantProps } from 'class-variance-authority';
import { Tabs as TabsPrimitive } from 'radix-ui';
import type { ComponentProps } from 'react';
import { cn } from '../../lib/cn';

/**
 * The tabs' root: holds the selected value (`value` or `defaultValue`) and the direction.
 * @param props Radix Tabs root props
 * @returns the root
 */
export function Tabs({ className, ...props }: ComponentProps<typeof TabsPrimitive.Root>) {
  return (
    <TabsPrimitive.Root
      data-slot="tabs"
      className={cn('flex min-w-0 flex-col gap-4', className)}
      {...props}
    />
  );
}

/** The tab list's classes by variant. */
export const tabsListVariants = cva('flex max-w-full min-w-0 items-stretch overflow-x-auto', {
  variants: {
    variant: {
      line: 'gap-4 border-b border-line',
      pill: 'w-fit gap-1 rounded-lg bg-subtle p-1',
    },
  },
  defaultVariants: { variant: 'line' },
});

/** The tab trigger's classes by the list's variant (shared with the route tabs of `PageTabs`). */
export const tabsTriggerVariants = cva(
  'inline-flex min-h-control-touch min-w-11 shrink-0 cursor-pointer items-center justify-center gap-2 whitespace-nowrap text-sm font-medium text-ink-secondary transition-colors hover:text-ink disabled:pointer-events-none disabled:opacity-50 lg:min-h-control lg:min-w-6 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        line: 'border-b-2 border-transparent px-1 data-[state=active]:border-primary data-[state=active]:text-ink aria-[current=page]:border-primary aria-[current=page]:text-ink',
        pill: 'rounded-sm px-3 data-[state=active]:bg-surface data-[state=active]:text-ink aria-[current=page]:bg-surface aria-[current=page]:text-ink lg:min-h-control-sm',
      },
    },
    defaultVariants: { variant: 'line' },
  },
);

/** Props of {@link TabsList}. */
export type TabsListProps = ComponentProps<typeof TabsPrimitive.List> &
  VariantProps<typeof tabsListVariants>;

/**
 * The row of tabs; it scrolls sideways inside itself when the tabs do not fit.
 * @param props the variant and Radix Tabs list props
 * @returns the list
 */
export function TabsList({ className, variant = 'line', ...props }: TabsListProps) {
  return (
    <TabsPrimitive.List
      data-slot="tabs-list"
      data-variant={variant}
      data-ui-scroll="x"
      className={cn(tabsListVariants({ variant }), className)}
      {...props}
    />
  );
}

/** Props of {@link TabsTrigger}. */
export type TabsTriggerProps = ComponentProps<typeof TabsPrimitive.Trigger> &
  VariantProps<typeof tabsTriggerVariants>;

/**
 * One tab.
 * @param props the variant (the list's) and Radix Tabs trigger props
 * @returns the tab
 */
export function TabsTrigger({ className, variant = 'line', ...props }: TabsTriggerProps) {
  return (
    <TabsPrimitive.Trigger
      data-slot="tabs-trigger"
      className={cn(tabsTriggerVariants({ variant }), className)}
      {...props}
    />
  );
}

/**
 * The panel of one tab.
 * @param props Radix Tabs content props
 * @returns the panel
 */
export function TabsContent({ className, ...props }: ComponentProps<typeof TabsPrimitive.Content>) {
  return (
    <TabsPrimitive.Content
      data-slot="tabs-content"
      // The panel takes the keyboard focus (Radix), so it is at least a target's size (010 revision 5).
      className={cn('min-h-control-touch min-w-0 flex-1 lg:min-h-6', className)}
      {...props}
    />
  );
}
